import {Box, IconButton, TextField, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, FormControl, Select, MenuItem, Avatar} from "@mui/material";
import {EditOutlined as EditIcon, DeleteOutline as DeleteIcon, Add as AddIcon, CategoryOutlined as CategoryIcon, CloudUpload as UploadIcon} from "@mui/icons-material";
import {useState, useEffect, useCallback, useImperativeHandle, forwardRef, useRef} from "react";
import {t} from "../dashboard/coach/coachTokens";
import MyDialog from "../shared/Dialog";
import {uploadImage} from "../../services/MediaUploadService";
import {useAuth} from "../../contexts/AuthContext";
import {useCompany} from "../../contexts/CompanyContext";
import {imageUrl as resizedImageUrl} from "../../utils/imageUrl";

// Fallback palette (used by Guides and any resource that doesn't define its own).
const DEFAULT_ICON_OPTIONS = [
    {value: "barbell-outline", label: "משקולות"},
    {value: "nutrition-outline", label: "תזונה"},
    {value: "medkit-outline", label: "שיקום"},
    {value: "body-outline", label: "מתיחות"},
    {value: "fitness-outline", label: "טכניקה"},
    {value: "walk-outline", label: "הליכה"},
    {value: "heart-outline", label: "בריאות"},
    {value: "bulb-outline", label: "מנטלי"},
    {value: "water-outline", label: "שתייה"},
    {value: "moon-outline", label: "שינה"},
    {value: "scale-outline", label: "משקל"},
    {value: "leaf-outline", label: "ספא"},
    {value: "hand-left-outline", label: "אומנויות לחימה"},
    {value: "book-outline", label: "מדריך"},
    {value: "grid-outline", label: "כללי"},
    {value: "flask-outline", label: "תוספים"},
    {value: "timer-outline", label: "טיימר"},
    {value: "flash-outline", label: "אנרגיה"},
    {value: "trophy-outline", label: "הישגים"},
];

/* Renders an Ionicons icon via the web component loaded in index.html */
const IonIcon = ({name, size = 22, color}) => (
    // eslint-disable-next-line
    <ion-icon name={name} style={{fontSize: size, color, display: "block"}}></ion-icon>
);

const td = {padding: "12px 14px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 14px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const ResourceCategoryManager = forwardRef(({config, companyId, onCategoriesChange}, ref) => {
    const {categoryService, labels} = config;
    const ICON_OPTIONS = config.iconOptions || DEFAULT_ICON_OPTIONS;
    const DEFAULT_ICON = config.defaultCategoryIcon || "book-outline";
    // Some resources (e.g. Recipes) manage categories by name only — hide the icon UI.
    const showCategoryIcons = config.showCategoryIcons !== false;

    const {isSuperAdmin} = useAuth();
    const {getCompaniesList} = useCompany();

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [name, setName] = useState("");
    const [icon, setIcon] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    // For super admins the category is company-scoped and can't inherit a company from the
    // account (they have none). Default to the company chosen in the top switcher when present.
    const [dialogCompanyId, setDialogCompanyId] = useState("");
    const [saving, setSaving] = useState(false);

    // Super admins must pick a company when creating (editing keeps the existing company).
    const requiresCompanySelection = isSuperAdmin() && !editingId;

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const res = await categoryService.getCategories(companyId);
            const list = res.data || [];
            setCategories(list);
            if (onCategoriesChange) onCategoriesChange(list);
        } catch (e) {
            console.error("Failed to fetch categories:", e);
        } finally {
            setLoading(false);
        }
    }, [categoryService, companyId, onCategoriesChange]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const openCreate = () => {
        setEditingId(null);
        setName("");
        setIcon(DEFAULT_ICON);
        setImageUrl("");
        setDialogCompanyId(companyId || "");
        setDialogOpen(true);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const cdnUrl = await uploadImage(file);
            if (cdnUrl) setImageUrl(cdnUrl);
        } catch (err) {
            console.error("Error uploading category image:", err);
        }
        setUploading(false);
    };

    // Expose openCreate to parent via ref
    useImperativeHandle(ref, () => ({
        openCreate
    }));

    const handleSave = async () => {
        if (!name.trim()) return;
        const effectiveCompanyId = isSuperAdmin() ? dialogCompanyId : companyId;
        if (requiresCompanySelection && !effectiveCompanyId) return;
        setSaving(true);
        try {
            if (editingId) {
                await categoryService.updateCategory(editingId, {name: name.trim(), icon: icon.trim() || DEFAULT_ICON, imageUrl: imageUrl || ""});
            } else {
                await categoryService.saveCategory({name: name.trim(), icon: icon.trim() || DEFAULT_ICON, imageUrl: imageUrl || "", companyId: effectiveCompanyId});
            }
            closeDialog();
            fetchCategories();
        } catch (e) {
            console.error("Failed to save category:", e);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(labels.categoryDeleteConfirm)) return;
        try {
            await categoryService.deleteCategory(id);
            fetchCategories();
        } catch (e) {
            console.error("Failed to delete category:", e);
        }
    };

    const startEdit = (cat) => {
        setEditingId(cat.id);
        setName(cat.name);
        setIcon(cat.icon);
        setImageUrl(cat.imageUrl || "");
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingId(null);
        setName("");
        setIcon("");
        setImageUrl("");
        setDialogCompanyId("");
    };

    return (
        <Box sx={{height: "100%", display: "flex", flexDirection: "column"}}>
            {/* Create/Edit dialog */}
            <MyDialog
                isOpen={dialogOpen}
                close={closeDialog}
                title={editingId ? "עריכת קטגוריה" : "הוספת קטגוריה"}
                showCloseButton={true}
                isTitleCentered={false}
                narrow={true}
                actions={
                    <Box
                        component="button"
                        onClick={handleSave}
                        disabled={!name.trim() || saving || (requiresCompanySelection && !dialogCompanyId)}
                        sx={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            background: t.green, color: "#fff", height: 38, px: "22px", borderRadius: t.rs,
                            fontSize: "14px", fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
                            transition: "all .14s",
                            "&:hover": {background: t.greenDark},
                            "&:disabled": {background: t.border, color: t.t3, cursor: "not-allowed"},
                        }}
                    >
                        {saving ? "שומר..." : editingId ? "עדכון" : "הוספה"}
                    </Box>
                }
            >
                <Box sx={{padding: "20px 0", display: "flex", flexDirection: "column", gap: "20px", direction: "rtl"}}>
                    {requiresCompanySelection && (
                        <Box>
                            <Typography sx={{fontSize: "14px", fontWeight: 600, color: t.text, mb: 1, textAlign: "right"}}>
                                חברה <span style={{color: t.red}}>*</span>
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={dialogCompanyId}
                                    onChange={e => setDialogCompanyId(e.target.value)}
                                    displayEmpty
                                    sx={{
                                        borderRadius: t.rs, backgroundColor: t.white,
                                        "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: t.green},
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: t.green},
                                    }}
                                >
                                    <MenuItem value="" disabled>בחר חברה</MenuItem>
                                    {getCompaniesList().map(comp => (
                                        <MenuItem key={comp.id} value={comp.id}>{comp.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                    <Box>
                        <Typography sx={{fontSize: "14px", fontWeight: 600, color: t.text, mb: 1, textAlign: "right"}}>
                            שם קטגוריה <span style={{color: t.red}}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="למשל: תזונה"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: t.rs, backgroundColor: t.white,
                                    "& fieldset": {borderColor: t.border},
                                    "&:hover fieldset": {borderColor: t.green},
                                    "&.Mui-focused fieldset": {borderColor: t.green},
                                }
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography sx={{fontSize: "14px", fontWeight: 600, color: t.text, mb: 1, textAlign: "right"}}>
                            תמונה
                        </Typography>
                        <Box sx={{display: "flex", alignItems: "center", gap: "14px"}}>
                            <Avatar
                                src={resizedImageUrl(imageUrl, 64) || undefined}
                                variant="rounded"
                                sx={{width: 64, height: 64, borderRadius: t.rs, border: `1px solid ${t.border}`, bgcolor: t.bg, color: t.t3}}
                            >
                                <CategoryIcon sx={{fontSize: 26, color: t.t3}}/>
                            </Avatar>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{display: "none"}}
                                onChange={handleImageUpload}
                            />
                            <Box
                                component="button"
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                sx={{
                                    display: "inline-flex", alignItems: "center", gap: "6px",
                                    background: t.white, color: t.text, height: 36, px: "14px", borderRadius: t.rs,
                                    fontSize: "13px", fontWeight: 600, border: `1px solid ${t.border}`, cursor: "pointer", fontFamily: "inherit",
                                    transition: "all .14s",
                                    "&:hover": {borderColor: t.green, color: t.green},
                                    "&:disabled": {opacity: 0.6, cursor: "not-allowed"},
                                }}
                            >
                                {uploading
                                    ? <CircularProgress size={16} sx={{color: t.green}}/>
                                    : <UploadIcon sx={{fontSize: 18}}/>}
                                {uploading ? "מעלה..." : imageUrl ? "החלפת תמונה" : "העלאת תמונה"}
                            </Box>
                            {imageUrl && !uploading && (
                                <IconButton size="small" onClick={() => setImageUrl("")} sx={{color: t.red, "&:hover": {bgcolor: t.redLight}}}>
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                    {showCategoryIcons && (
                    <Box>
                        <Typography sx={{fontSize: "14px", fontWeight: 600, color: t.text, mb: 1, textAlign: "right"}}>
                            אייקון
                        </Typography>
                        <Box sx={{display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1}}>
                            {ICON_OPTIONS.map(opt => {
                                const isSelected = icon === opt.value;
                                return (
                                    <Tooltip key={opt.value} title={opt.label} placement="top">
                                        <Box
                                            onClick={() => setIcon(opt.value)}
                                            sx={{
                                                display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5,
                                                py: 1.5, borderRadius: t.rs, cursor: "pointer",
                                                border: isSelected ? `2px solid ${t.green}` : `1px solid ${t.border}`,
                                                bgcolor: isSelected ? t.greenLight : t.white,
                                                transition: "all 0.15s ease",
                                                "&:hover": {borderColor: t.green, bgcolor: t.greenLight},
                                            }}
                                        >
                                            <IonIcon name={opt.value} size={22} color={isSelected ? t.green : t.t2}/>
                                            <Typography sx={{fontSize: "10px", color: isSelected ? t.greenText : t.t3, fontWeight: isSelected ? 600 : 400}}>
                                                {opt.label}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Box>
                    )}
                </Box>
            </MyDialog>

            {/* Card */}
            <Box sx={{bgcolor: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, overflow: "hidden", boxShadow: t.sh}}>
                {/* Header band */}
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 18px", borderBottom: `1px solid ${t.border}`, background: t.bg, direction: "rtl",
                }}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                        <Box sx={{width: 36, height: 36, borderRadius: "10px", background: t.white, border: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                            <CategoryIcon sx={{color: t.t2, fontSize: 20}}/>
                        </Box>
                        <Box>
                            <Typography sx={{fontSize: 15, fontWeight: 800, color: t.text, letterSpacing: "-0.2px"}}>{labels.categoriesCardTitle}</Typography>
                            <Typography sx={{fontSize: 11.5, color: t.t3, fontWeight: 600}}>{categories.length} קטגוריות</Typography>
                        </Box>
                    </Box>
                    <Box component="button" onClick={openCreate} sx={{
                        display: "inline-flex", alignItems: "center", gap: "5px",
                        background: t.green, color: "#fff", height: 34, px: "15px", borderRadius: t.rs,
                        fontSize: "13px", fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
                        transition: "all .14s", "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                    }}>
                        <AddIcon sx={{fontSize: 16}}/>
                        {labels.newCategoryButton}
                    </Box>
                </Box>

                {loading && !categories.length ? (
                    <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, py: 6}}>
                        <CircularProgress size={32} sx={{color: t.green}}/>
                    </Box>
                ) : !categories.length ? (
                    <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, py: 6}}>
                        <CategoryIcon sx={{fontSize: 48, color: t.border}}/>
                        <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>אין קטגוריות</Typography>
                        <Typography sx={{color: t.t3, fontSize: "13px"}}>לחץ על "{labels.newCategoryButton}" כדי להוסיף</Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{overflowY: "auto", maxHeight: 540, direction: "rtl"}}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{...th, textAlign: "center", width: 70}}>תמונה</TableCell>
                                    {showCategoryIcons && (
                                        <TableCell sx={{...th, textAlign: "center", width: 70}}>אייקון</TableCell>
                                    )}
                                    <TableCell sx={{...th, textAlign: "right"}}>שם</TableCell>
                                    <TableCell sx={{...th, textAlign: "center", width: 110}}>פעולות</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {categories.map(cat => (
                                    <TableRow key={cat.id} sx={{"&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}>
                                        <TableCell sx={{...td, textAlign: "center"}}>
                                            <Avatar
                                                src={resizedImageUrl(cat.imageUrl, 36) || undefined}
                                                variant="rounded"
                                                sx={{width: 36, height: 36, borderRadius: t.rs, border: `1px solid ${t.border}`, bgcolor: t.bg, color: t.t3, display: "inline-flex", verticalAlign: "middle"}}
                                            >
                                                <CategoryIcon sx={{fontSize: 18, color: t.t3}}/>
                                            </Avatar>
                                        </TableCell>
                                        {showCategoryIcons && (
                                            <TableCell sx={{...td, textAlign: "center"}}>
                                                <IonIcon name={cat.icon || DEFAULT_ICON} size={22} color={t.t2}/>
                                            </TableCell>
                                        )}
                                        <TableCell sx={{...td, textAlign: "right", fontWeight: 600, color: t.text}}>
                                            {cat.name}
                                        </TableCell>
                                        <TableCell sx={{...td, textAlign: "center"}}>
                                            <Box sx={{display: "inline-flex", justifyContent: "center", gap: "2px"}}>
                                                <IconButton size="small" onClick={() => startEdit(cat)} sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                                    <EditIcon fontSize="small"/>
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDelete(cat.id)} sx={{color: t.red, "&:hover": {bgcolor: t.redLight}}}>
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
});

export default ResourceCategoryManager;
